import {
  Conversation,
  type ConversationChannel,
  type ConversationSyncStorageMode,
  storedConversations,
} from '@stripe-internal/conversations-client';

export const LIVE_SUPPORT_CHANNELS = ['chat', 'request_a_call'] as const;

export type LiveSupportChannel = (typeof LIVE_SUPPORT_CHANNELS)[number];

export function isLiveSupportChannel(
  channel: unknown,
): channel is LiveSupportChannel {
  return channel === 'chat' || channel === 'request_a_call';
}

export function syncStorageModeForThreadsChannel(
  _channel: ConversationChannel,
  syncStorageMode: ConversationSyncStorageMode,
  withHistory: boolean,
): ConversationSyncStorageMode {
  // Threads conversations (including live-support channels, which are
  // seeded on resume) are resumed from the backend, so nothing is
  // persisted to storage when history is enabled.
  return withHistory ? false : syncStorageMode;
}

export function clearStoredConversation(id: string): void {
  Conversation.clearStorage({
    prefix: Conversation.storageKey(id),
    all: true,
  });
}

// Clears the legacy stored conversations this surface previously wrote, now
// that Threads resumes from the backend instead. Deliberately not
// `Conversation.clearStorage({all: true})`: the conversation storage namespace
// is shared across every assistant surface on the origin, so an unscoped wipe
// would drop the in-progress conversation of a co-mounted non-history surface
// (e.g. the dotcom widget). Entries without an owner recorded in their stored
// context are left alone and expire on their own TTL.
export function clearStoredConversations({
  audience,
  entryPoint,
}: {
  audience?: string;
  entryPoint?: string;
}): void {
  if (!audience || !entryPoint) {
    // without an owner to match on, every match would be a guess
    return;
  }

  storedConversations({includeInactive: true}).forEach((stored) => {
    const {context} = stored.data;
    if (
      context == null ||
      context.audience !== audience ||
      context.entryPoint !== entryPoint
    ) {
      return;
    }

    clearStoredConversation(stored.id);
  });
}
